/**
 * MP4 Fast-Start (qt-faststart) 구현
 *
 * MP4/MOV 파일의 moov atom을 파일 앞쪽으로 이동하여
 * HTTP Range Request 기반 스트리밍 성능을 개선합니다.
 *
 * moov가 이미 앞에 있으면 원본을 그대로 반환합니다.
 *
 * Streaming 방식: 전체 파일을 메모리에 올리지 않고 Blob.slice()로
 * 필요한 부분(moov atom)만 읽어서 처리합니다.
 * → 파일 크기 제한 없이 동작 (메모리 사용량 = moov 크기, 보통 수백KB~수MB)
 */

const MOOV = 0x6d6f6f76; // 'moov'
const MDAT = 0x6d646174; // 'mdat'
const FREE = 0x66726565; // 'free'
const WIDE = 0x77696465; // 'wide'

interface BoxInfo {
  type: number;
  offset: number;
  size: number;
}

/**
 * Blob의 특정 범위만 ArrayBuffer로 읽기.
 * 전체 파일을 메모리에 올리지 않음.
 */
async function readBlobRange(
  blob: Blob,
  offset: number,
  length: number,
): Promise<ArrayBuffer> {
  const slice = blob.slice(offset, offset + length);
  return slice.arrayBuffer();
}

/**
 * top-level atom 구조를 스트리밍 방식으로 파싱.
 * 파일 시작부터 atom 헤더(8~16바이트)만 순차 읽기.
 * mdat 같은 거대 atom의 데이터는 읽지 않고 크기만 파악.
 */
async function parseTopLevelBoxesStreaming(file: Blob): Promise<BoxInfo[]> {
  const boxes: BoxInfo[] = [];
  let offset = 0;
  const fileSize = file.size;

  while (offset < fileSize - 8) {
    // atom 헤더만 읽기 (최대 16바이트 — extended size 대비)
    const headerSize = Math.min(16, fileSize - offset);
    const headerBuf = await readBlobRange(file, offset, headerSize);
    const view = new DataView(headerBuf);

    let size = view.getUint32(0, false);
    const type = view.getUint32(4, false);

    if (size === 0) break;

    if (size === 1) {
      // 64-bit extended size
      if (headerSize < 16) break;
      const hi = view.getUint32(8, false);
      const lo = view.getUint32(12, false);
      size = hi * 0x100000000 + lo;
    }

    if (size < 8) break;

    boxes.push({ type, offset, size });

    offset += size;
  }

  return boxes;
}

/**
 * moov 내부의 stco/co64 chunk offset을 패치.
 * moov atom만 메모리에 올려서 처리 (보통 수백KB~수MB).
 */
function patchMoovOffsets(
  moovData: Uint8Array,
  offsetShift: number,
): ArrayBuffer {
  const patched = new Uint8Array(moovData);
  const view = new DataView(
    patched.buffer,
    patched.byteOffset,
    patched.byteLength,
  );

  const CONTAINER_BOXES = new Set([
    0x6d6f6f76, // moov
    0x7472616b, // trak
    0x6d646961, // mdia
    0x6d696e66, // minf
    0x7374626c, // stbl
    0x65647473, // edts
    0x75647461, // udta
    0x64696e66, // dinf
    0x6d766578, // mvex
  ]);

  const patchBox = (start: number, end: number) => {
    let pos = start;
    while (pos < end - 8) {
      const rawSize = view.getUint32(pos, false);
      const boxType = view.getUint32(pos + 4, false);
      let boxSize = rawSize;
      const isExtended = rawSize === 1;

      if (rawSize === 0) break;
      if (isExtended) {
        if (pos + 16 > end) break;
        const hi = view.getUint32(pos + 8, false);
        const lo = view.getUint32(pos + 12, false);
        boxSize = hi * 0x100000000 + lo;
      }
      if (boxSize < 8 || pos + boxSize > end) break;

      const STCO = 0x7374636f; // 'stco'
      const CO64 = 0x636f3634; // 'co64'

      if (boxType === STCO) {
        const entryCount = view.getUint32(pos + 12, false);
        for (let i = 0; i < entryCount; i++) {
          const entryOffset = pos + 16 + i * 4;
          if (entryOffset + 4 > end) break;
          const oldVal = view.getUint32(entryOffset, false);
          view.setUint32(entryOffset, oldVal + offsetShift, false);
        }
      } else if (boxType === CO64) {
        const entryCount = view.getUint32(pos + 12, false);
        for (let i = 0; i < entryCount; i++) {
          const entryOffset = pos + 16 + i * 8;
          if (entryOffset + 8 > end) break;
          const hi = view.getUint32(entryOffset, false);
          const lo = view.getUint32(entryOffset + 4, false);
          const oldVal = hi * 0x100000000 + lo;
          const newVal = oldVal + offsetShift;
          view.setUint32(entryOffset, (newVal / 0x100000000) >>> 0, false);
          view.setUint32(entryOffset + 4, newVal >>> 0, false);
        }
      } else if (CONTAINER_BOXES.has(boxType)) {
        const headerSize = isExtended ? 16 : 8;
        const childStart = pos + headerSize;
        if (childStart < pos + boxSize) {
          patchBox(childStart, pos + boxSize);
        }
      }

      pos += boxSize;
    }
  };

  patchBox(8, patched.byteLength);
  return patched.buffer as ArrayBuffer;
}

function isMp4OrMov(file: File): boolean {
  const name = file.name.toLowerCase();
  return (
    name.endsWith('.mp4') ||
    name.endsWith('.mov') ||
    name.endsWith('.m4v') ||
    name.endsWith('.m4a') ||
    file.type === 'video/mp4' ||
    file.type === 'video/quicktime'
  );
}

/**
 * MP4/MOV 파일의 moov atom을 파일 앞쪽으로 이동합니다.
 *
 * Streaming 방식으로 동작:
 * - atom 헤더만 순차 읽어서 구조 파악
 * - moov atom만 메모리에 올려서 stco/co64 패치
 * - Blob.slice()로 mdat 등 거대 atom은 메모리에 올리지 않음
 * - 파일 크기 제한 없음 (메모리 사용 = moov 크기)
 *
 * moov가 이미 mdat 앞에 있으면 원본 File을 그대로 반환합니다.
 * MP4/MOV가 아닌 파일은 원본을 그대로 반환합니다.
 */
export async function applyFastStart(file: File): Promise<File> {
  if (!isMp4OrMov(file)) {
    return file;
  }

  // Streaming 방식으로 atom 구조 파악 (헤더만 읽음)
  const boxes = await parseTopLevelBoxesStreaming(file);

  const moovBox = boxes.find((b) => b.type === MOOV);
  const mdatBox = boxes.find((b) => b.type === MDAT);

  if (!moovBox || !mdatBox) {
    return file;
  }

  // 이미 fast-start이면 원본 반환
  if (moovBox.offset < mdatBox.offset) {
    return file;
  }

  // moov atom만 메모리에 올려서 stco/co64 패치
  const moovBuffer = await readBlobRange(file, moovBox.offset, moovBox.size);
  const moovData = new Uint8Array(moovBuffer);
  const offsetShift = moovBox.size;
  const patchedMoov = patchMoovOffsets(moovData, offsetShift);

  // Blob.slice()로 재조립 — mdat은 메모리에 올리지 않음
  const parts: BlobPart[] = [];

  // 1. mdat 앞의 atom들 (ftyp, free 등) — moov 제외
  for (const box of boxes) {
    if (box.offset < mdatBox.offset && box.type !== MOOV) {
      parts.push(file.slice(box.offset, box.offset + box.size));
    }
  }

  // 2. 패치된 moov
  parts.push(patchedMoov);

  // 3. mdat (Blob.slice — 메모리에 올리지 않음)
  parts.push(file.slice(mdatBox.offset, mdatBox.offset + mdatBox.size));

  // 4. mdat 뒤의 기타 atom (moov, free, wide 제외)
  for (const box of boxes) {
    if (
      box.offset > mdatBox.offset &&
      box.type !== MOOV &&
      box.type !== FREE &&
      box.type !== WIDE
    ) {
      parts.push(file.slice(box.offset, box.offset + box.size));
    }
  }

  const blob = new Blob(parts, { type: file.type || 'video/mp4' });
  return new File([blob], file.name, {
    type: file.type || 'video/mp4',
    lastModified: file.lastModified,
  });
}
